import axios from "axios";
import React, { useEffect, useState, useMemo } from "react";

import PageOption from "./../PageTypes/PageOption";

import OnboardPanel from "./OnboardPanelComponents/OnboardPanel";

import DetailsPanel from "./DetailsPanelComponents/DetailsPanel";
import LearnPanel from "./LearnPanelComponents/LearnPanel";
import OverviewPanel from "./OverviewPanelComponents/OverviewPanel";
import ScenariosPanel from "./ScenariosPanelComponents/ScenariosPanel";
import StrategiesPanel from "./StrategiesPanelComponents/StrategiesPanel";
import TradePanel from "./TradePanelComponents/TradePanel";
// import PlaidPanel from "./PlaidPanelComponents/PlaidPanel";
import ProfilePanel from "./ProfilePanelComponents/ProfilePanel";
import { defaultNoStrategyDisplayProps } from "./StrategiesPanelComponents/DefaultNoStrategyDisplayProps";

import SelectedStrategy from "./StrategiesPanelComponents/SelectedStrategy";
import updateStrategyGraphIfNotUpdated from "./StrategiesPanelComponents/updateStrategyGraphIfNotUpdated";

import { defaultNoScenarioDisplayProps } from "./ScenariosPanelComponents/DefaultNoScenarioDisplayProps";
import SelectedScenario from "./ScenariosPanelComponents/SelectedScenario";
import updateScenarioGraphIfNotUpdated from "./ScenariosPanelComponents/updateScenarioGraphIfNotUpdated";

import FormTestPanel from "./../FormTestPanel/FormTestPanel";
import ThemeTestPanel from "./../ThemeSettings/ThemeTestPanel";

import StockPlan from "./../StockPlanTypes/StockPlan";
import StockVest from "./../StockPlanTypes/StockVest";
import SymbolOption from "./UtilityComponents/SymbolUtilityComponents/SymbolOption";

import dayjs from "dayjs";
import FrequencyType from "./OverviewPanelComponents/FrequencyType";
import PeriodType from "./UtilityComponents/GraphOptionUtilityComponents/PeriodType";

import ConfirmDialogProps from "../NavigationComponents/ConfirmDialogProps";
import DefaultConfiguredFormState from "./UtilityComponents/FormUtilityComponents/DefaultConfiguredFormState";
import FormConfig from "./UtilityComponents/FormUtilityComponents/FormConfig";
import FormRowState from "./UtilityComponents/FormUtilityComponents/FormRowState";

import useTourStep from "./UtilityComponents/TourComponents/useTourStep";

import { Route, Routes } from "react-router-dom";
import ErrorPage from "../ErrorPageComponents/ErrorPage";

import snackbarStrategyChange from "./StrategiesPanelComponents/snackbarStrategyChange";
import snackbarScenarioChange from "./ScenariosPanelComponents/snackbarScenarioChange";
import useAlertMultiMessageSnackbar from "./UtilityComponents/SnackbarUtilityComponents/useAlertMultiMessageSnackbar";

import exactSymbolMatch from "./UtilityComponents/SymbolUtilityComponents/exactSymbolMatch";
import StrategySuggestions from "./StrategiesPanelComponents/StrategySuggestions";
import PostSelectedStrategyList from "./StrategiesPanelComponents/PostSelectedStrategyList";
import ScenarioSuggestions from "./ScenariosPanelComponents/ScenarioSuggestions";
import PostSelectedScenarioList from "./ScenariosPanelComponents/PostSelectedScenarioList";

import GetCSRFToken from "../AuthComponents/GetCSRFToken";
import ESPPPanel from "./ESPPPanelComponents/ESPPPanel";
import ESPP from "../ESPPTypes/ESPP";
import ESPPExplorerPanel from "./ESPPExplorerComponents/ESPPExplorerPanel";
import ESPPPurchasePeriodType from "./ESPPExplorerComponents/ESPPPurchasePeriodType";
import getNextPurchasePeriod from "./ESPPExplorerComponents/getNextPurchasePeriod";

function numberFromBackendStringMakeNullNegativeOne(value: string | undefined | null) {
  return value === undefined || value === null ? -1 : Number(value);
}

// TODO: typescript better than the any type
function inProcessStockPlanResponse(response: any): StockPlan[] {
  const stock_plans_list_json = response.data.rsu_plans;
  const typed_stock_plans_list_json = stock_plans_list_json.map((sp: any) => ({
    ...sp,
    start_date: dayjs(sp.start_date),
    stock_vests: sp.stock_vests.map((sv: StockVest) => ({
      ...sv,
      date: dayjs(sv.date),
    })),
  }));
  return typed_stock_plans_list_json.sort(function (a: StockPlan, b: StockPlan) {
    return b.start_date.diff(a.start_date);
  }); // TODO: add a second layer of sorting from start date to value?
}

function inProcessESPPPeriodResponse(response: any): ESPPPurchasePeriodType[] {
  const espp_period_json = response.data.purchase_periods;
  const typed_espp_period_json = espp_period_json.map((espp_period: any) => ({
    ...espp_period,
    enroll_date: dayjs(espp_period.enroll_date),
    offering_date: dayjs(espp_period.offering_date),
    purchase_start_date: dayjs(espp_period.purchase_start_date),
    purchase_date: dayjs(espp_period.purchase_date),
  }));
  return typed_espp_period_json.sort(function (a: ESPPPurchasePeriodType, b: ESPPPurchasePeriodType) {
    // sort by reverse offering date first then purchase date
    if (a.offering_date.isSame(b.offering_date)) {
      return a.purchase_date.diff(b.purchase_date);
    } else {
      return b.offering_date.diff(a.offering_date);
    }
  });
}

// TODO: typescript better than the any type
function inProcessESPPResponse(response: any): ESPP[] {
  const espps_list_json = response.data.espp_plans;
  const typed_espp_list_json = espps_list_json.map((espp: any) => ({
    ...espp,
    offering_date: dayjs(espp.offering_date),
    purchase_period_start: dayjs(espp.purchase_period_start),
    purchase_date: dayjs(espp.purchase_date),
  }));
  return typed_espp_list_json.sort(function (a: ESPP, b: ESPP) {
    return b.purchase_date.diff(a.purchase_date);
  });
}

type MainPanelProps = {
  currentPage: PageOption;
  setPage: (newPage: PageOption) => void;
  setConfirmOnNavigateAway: (newConfirmOnNavigateAway: Partial<ConfirmDialogProps> | false) => void;
  user: string | undefined;
  emailVerified: boolean;
  drawerWidth: number;
  validAppRoutes: PageOption[];
  skipTour: boolean;
  finishTour: () => void;
  nextButtonForTourStepAction: (action: (() => void) | undefined) => void;
  setNextButtonForTourStepText: (newText: string) => void;
  setHasForkedTour: (hasForkedTour: boolean) => void;
  setTourForkAction: (action: (() => void) | undefined) => void;
  setTourForkButtonText: (newText: string) => void;
};

function MainPanel(props: MainPanelProps) {
  // Snackbar
  const { addSnackbarMessage, AlertMultiMessageSnackbarComponent } = useAlertMultiMessageSnackbar();

  //Need Refresh State
  const [lastStockPlanUpdateTime, setLastStockPlanUpdateTime] = React.useState(dayjs());

  // Strategies Panel State that is needed elsewhere
  const [firstConfiguredStrategiesCheckDone, setFirstConfiguredStrategiesCheckDone] = useState(false);

  //Overview Panel State
  const [overviewPanelLastDrawnPercentVested, setOverviewPanelLastDrawnPercentVested] = useState<number | undefined>(
    undefined
  );
  const [statisticsRetrievalErrored, setStatisticsRetrievalErrored] = useState(false);
  const [totalStockValue, setTotalStockValue] = useState(-1);
  const [lastDrawnTotalStockValue, setLastDrawnTotalStockValue] = useState<number | undefined>(undefined);
  const [vestedValue, setVestedValue] = useState(-1);
  const [lastDrawnVestedValue, setLastDrawnVestedValue] = useState<number | undefined>(undefined);
  const [percentVested, setPercentVested] = useState(-1);
  const [daysUntilNextTrade, setDaysUntilNextTrade] = useState(-1);
  const [nextTradeMoreInfoTitle, setNextTradeMoreInfoTitle] = useState("");
  const [nextTradeMoreInfoString, setNextTradeMoreInfoString] = useState("");
  const [lastDrawnDaysUntilNextTrade, setLastDrawnDaysUntilNextTrade] = useState<number | undefined>(undefined);
  const [daysUntilNextVest, setDaysUntilNextVest] = useState(-1);
  const [nextVestMoreInfoTitle, setNextVestMoreInfoTitle] = useState("");
  const [nextVestMoreInfoString, setNextVestMoreInfoString] = useState("");
  const [lastDrawnDaysUntilNextVest, setLastDrawnDaysUntilNextVest] = useState<number | undefined>(undefined);
  const [valueSold, setValueSold] = useState(-1);
  const [lastDrawnValueSold, setLastDrawnValueSold] = useState<number | undefined>(undefined);
  const [referencePrice, setReferencePrice] = useState(-1);
  const [lastDrawnReferencePrice, setLastDrawnReferencePrice] = useState<number | undefined>(undefined);
  const [referenceDateString, setReferenceDateString] = useState("");
  const [overviewPlotFrequency, setOverviewPlotFrequency] = useState<FrequencyType>("daily");
  const [overviewPlotHistoricalPeriod, setOverviewPlotHistoricalPeriod] = useState<PeriodType>("2 Yr");
  const [overviewPlotFuturePeriod, setOverviewPlotFuturePeriod] = useState<PeriodType>("Max");
  const [overviewPlotError, setOverviewPlotError] = useState(false);

  // Stats for Details Panel
  const [daysUntilNextVestRSU, setDaysUntilNextVestRSU] = useState(-1);
  const [vestedValueRSU, setVestedValueRSU] = useState(-1);
  const [vestedValueGrossRSU, setVestedValueGrossRSU] = useState(-1);
  const [totalStockValueRSU, setTotalStockValueRSU] = useState(-1);
  const [totalStockValueGrossRSU, setTotalStockValueGrossRSU] = useState(-1);
  const [percentVestedRSU, setPercentVestedRSU] = useState(-1);
  const [totalSharesSoldRSU, setTotalSharesSoldRSU] = useState(-1);

  // Stats for ESPP Panel
  const [daysUntilNextVestESPP, setDaysUntilNextVestESPP] = useState(-1);
  const [vestedValueESPP, setVestedValueESPP] = useState(-1);
  const [vestedValueGrossESPP, setVestedValueGrossESPP] = useState(-1);
  const [totalStockValueESPP, setTotalStockValueESPP] = useState(-1);
  const [totalStockValueGrossESPP, setTotalStockValueGrossESPP] = useState(-1);
  const [percentVestedESPP, setPercentVestedESPP] = useState(-1);
  const [totalSharesSoldESPP, setTotalSharesSoldESPP] = useState(-1);

  function emptyOverviewPlotJsonAndUpdated() {
    return {
      json: {
        data: [],
        layout: { title: "Overview Loading" },
        config: {},
      },
      updated: false,
    };
  }
  const [overviewPlotJsonAndUpdated, setOverviewPlotJsonAndUpdated] = useState<{
    [key: string]: any;
  }>(emptyOverviewPlotJsonAndUpdated());

  // Forces stock plan updates on new user login
  useEffect(() => {
    if (props.user === undefined) {
      setLastStockPlanUpdateTime(dayjs()); // tells everyone to update now that user is logged out
    }
  }, [props.user]);

  //Overview Panel Backend Calls
  const [lastOverviewGraphUpdateTime, setLastOverviewGraphUpdateTime] = useState(dayjs());
  useEffect(() => {
    if (props.user !== undefined) {
      setOverviewPlotError(false);
      setOverviewPlotJsonAndUpdated((oldState) => {
        return {
          ...oldState,
          updated: false,
        };
      });
      axios
        .get("/api/overview/summary", {
          params: {
            frequency: overviewPlotFrequency,
          },
        })
        .then((response) => {
          setOverviewPlotError(false);
          setDaysUntilNextTrade(numberFromBackendStringMakeNullNegativeOne(response.data["days_until_next_trade"]));
          setNextTradeMoreInfoTitle(response.data["next_trade_more_info_title"]);
          setNextTradeMoreInfoString(response.data["next_trade_more_info"]);
          setNextVestMoreInfoTitle(response.data["next_vest_more_info_title"]);
          setNextVestMoreInfoString(response.data["next_vest_more_info"]);
          setOverviewPlotJsonAndUpdated({
            json: response.data["graph_json"],
            updated: true,
          });
        })
        .catch((error) => {
          setOverviewPlotError(true);
          if (process.env.NODE_ENV === "development") {
            console.log(error);
          }
        });
    } else {
      // gets rid of data on logout
      setOverviewPlotJsonAndUpdated(emptyOverviewPlotJsonAndUpdated());
    }
  }, [
    lastStockPlanUpdateTime,
    lastOverviewGraphUpdateTime,
    overviewPlotFrequency,
    setOverviewPlotError,
    setOverviewPlotJsonAndUpdated,
    props.user,
  ]); // second argument means only rerun if something in that array changes

  useEffect(() => {
    if (props.user !== undefined) {
      setStatisticsRetrievalErrored(false);
      axios
        .get("/api/overview/statistics")
        .then((response) => {
          setTotalStockValue(numberFromBackendStringMakeNullNegativeOne(response.data["total_stock_value"]));
          setVestedValue(numberFromBackendStringMakeNullNegativeOne(response.data["vested_value"]));
          setPercentVested(numberFromBackendStringMakeNullNegativeOne(response.data["fraction_vested"]));
          setValueSold(numberFromBackendStringMakeNullNegativeOne(response.data["value_sold"]));
          setDaysUntilNextVest(numberFromBackendStringMakeNullNegativeOne(response.data["days_until_next_vest"]));
          setReferencePrice(numberFromBackendStringMakeNullNegativeOne(response.data["reference_price"]));
          setReferenceDateString(response.data["reference_date_string"]);

          setDaysUntilNextVestRSU(
            numberFromBackendStringMakeNullNegativeOne(response.data["rsu"]["days_until_next_vest"])
          );
          setVestedValueRSU(numberFromBackendStringMakeNullNegativeOne(response.data["rsu"]["vested_value"]));
          setVestedValueGrossRSU(
            numberFromBackendStringMakeNullNegativeOne(response.data["rsu"]["vested_value_gross"])
          );
          setTotalStockValueRSU(numberFromBackendStringMakeNullNegativeOne(response.data["rsu"]["total_stock_value"]));
          setTotalStockValueGrossRSU(
            numberFromBackendStringMakeNullNegativeOne(response.data["rsu"]["total_stock_value_gross"])
          );
          setPercentVestedRSU(numberFromBackendStringMakeNullNegativeOne(response.data["rsu"]["fraction_vested"]));
          setTotalSharesSoldRSU(numberFromBackendStringMakeNullNegativeOne(response.data["rsu"]["total_shares_sold"]));

          setDaysUntilNextVestESPP(
            numberFromBackendStringMakeNullNegativeOne(response.data["espp"]["days_until_next_vest"])
          );
          setVestedValueESPP(numberFromBackendStringMakeNullNegativeOne(response.data["espp"]["vested_value"]));
          setVestedValueGrossESPP(
            numberFromBackendStringMakeNullNegativeOne(response.data["espp"]["vested_value_gross"])
          );
          setTotalStockValueESPP(
            numberFromBackendStringMakeNullNegativeOne(response.data["espp"]["total_stock_value"])
          );
          setTotalStockValueGrossESPP(
            numberFromBackendStringMakeNullNegativeOne(response.data["espp"]["total_stock_value_gross"])
          );
          setPercentVestedESPP(numberFromBackendStringMakeNullNegativeOne(response.data["espp"]["fraction_vested"]));
          setTotalSharesSoldESPP(
            numberFromBackendStringMakeNullNegativeOne(response.data["espp"]["total_shares_sold"])
          );
        })
        .catch((error) => {
          if (process.env.NODE_ENV === "development") {
            console.log(error);
          }
          setStatisticsRetrievalErrored(true);
        });
    } else {
      // gets rid of data on logout
      setTotalStockValue(-1);
      setVestedValue(-1);
      setDaysUntilNextVest(-1);
      setValueSold(-1);
      setReferencePrice(-1);
    }
  }, [lastStockPlanUpdateTime, lastOverviewGraphUpdateTime, props.user]);

  //Details Panel State
  const [firstDetailsPanelCheckDone, setFirstDetailsPanelCheckDone] = useState(false);
  const [userSymbolOption, setUserSymbolOption] = useState<undefined | SymbolOption>(undefined); // undefined means that the query for finding the data is running
  const [firstUserSymbolCheckDone, setFirstUserSymbolCheckDone] = useState(false);
  const [dialogPromptForSymbol, setDialogPromptForSymbol] = useState(true);
  const [stockPlanSelectorConfig, setStockPlanSelectorConfig] = useState<FormConfig | undefined>(undefined);
  const [defaultStockPlanSelectorForm, setDefaultStockPlanSelectorForm] = useState<FormConfig | undefined>(undefined);
  const [defaultStockPlanSelectState, setDefaultStockPlanSelectorState] = useState<FormRowState[]>([]);
  const [stockPlanSelectorState, setStockPlanSelectorState] = useState<FormRowState[]>([]);
  const [detailsStockPlans, setDetailsStockPlans] = React.useState<StockPlan[]>([]);
  const [detailsStockPrice, setDetailsStockPrice] = React.useState<number | undefined>(undefined); // undefined means that the query for finding the data is running
  const [detailsStockPlanLoading, setDetailsStockPlanLoading] = React.useState(false);
  const [detailsStockPlanError, setDetailsStockPlanError] = React.useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [detailsPanelExternalStartAdd, setDetailsPanelExternalStartAdd] = useState(false);
  const [detailsPanelLastDrawnReferencePrice, setDetailsPanelLastDrawnReferencePrice] = useState<number | undefined>(
    undefined
  );
  const [detailsPanelLastDrawnVestedValue, setDetailsPanelLastDrawnVestedValue] = useState<number | undefined>(
    undefined
  );
  const [detailsPanelLastDrawnSharesSold, setDetailsPanelLastDrawnSharesSold] = useState<number | undefined>(undefined);
  const [detailsPanelLastDrawnPercentVested, setDetailsPanelLastDrawnPercentVested] = useState<number | undefined>(
    undefined
  );
  const [detailsPanelLastDrawnDaysUntilNextVest, setDetailsPanelLastDrawnDaysUntilNextVest] = useState<
    number | undefined
  >(undefined);
  const [detailsPanelLastDrawnTotalStockValue, setDetailsPanelLastDrawnTotalStockValue] = useState<number | undefined>(
    undefined
  );

  //Details Panel Backend Calls
  // this state is for profile but declaring it up here for dependency reasons
  const [backendInputRowStateList, setBackendInputRowStateList] = useState<FormRowState[] | undefined>(undefined); // undefined means profile not valid in backend

  useEffect(() => {
    if (!detailsDialogOpen) {
      setDialogPromptForSymbol(userSymbolOption === undefined);
    }
  }, [firstUserSymbolCheckDone, userSymbolOption, detailsDialogOpen]);

  useEffect(() => {
    axios
      .get("api/stockplan/symbol")
      .then((res) => {
        setUserSymbolOption(exactSymbolMatch(res.data));
      })
      .catch((res) => {
        setUserSymbolOption(undefined);
      })
      .finally(() => {
        setFirstUserSymbolCheckDone(true);
      });
  }, [backendInputRowStateList]);

  useEffect(() => {
    if (props.user !== undefined) {
      axios
        .get("/api/stockplan/form")
        .then((response) => {
          setStockPlanSelectorConfig({ ...response.data, rowStates: undefined });
          setDefaultStockPlanSelectorForm({ ...response.data, rowStates: undefined });
          if ("rowStates" in response.data) {
            setDefaultStockPlanSelectorState(response.data["rowStates"]);
            setStockPlanSelectorState(response.data["rowStates"]);
          } else {
            setDefaultStockPlanSelectorState(DefaultConfiguredFormState(response.data));
            setStockPlanSelectorState(DefaultConfiguredFormState(response.data));
          }
        })
        .catch((error) => console.error(error));
    }
  }, [props.user, userSymbolOption]);
  useEffect(() => {
    if (props.user !== undefined) {
      setDetailsStockPlanLoading(true);
      setDetailsStockPlanError(false);
      axios
        .get("/api/stockplan")
        .then((response) => {
          setDetailsStockPlanError(false);
          setDetailsStockPlanLoading(false);
          const stockPlans = inProcessStockPlanResponse(response);
          setDetailsStockPlans(stockPlans);
          setFirstDetailsPanelCheckDone(true);
        })
        .catch((error) => {
          setDetailsStockPlanLoading(false);
          setDetailsStockPlanError(true);
          if (process.env.NODE_ENV === "development") {
            console.log(error);
          }
        });
    } else {
      setDetailsStockPlans([]);
    }
  }, [lastStockPlanUpdateTime, props.user]); // second argument means only rerun if something in that array changes

  useEffect(() => {
    if (detailsStockPrice === undefined || detailsStockPrice === -1) {
      setDetailsStockPrice(undefined); // puts the stock price table in a 'loading' state if there was no stock price before
    }
    axios // call this in case the user changes the ticker in the profile
      .get("/api/overview/price")
      .then((response) => {
        const price = response.data["last_price"];
        if (price !== detailsStockPrice) {
          setDetailsStockPrice(price);
        }
      })
      .catch((error) => {
        if (process.env.NODE_ENV === "development") {
          console.error(error);
        }
        setDetailsStockPrice(-1);
      }); // TODO: better error handling
  }, [lastStockPlanUpdateTime, setDetailsStockPrice]);

  // ESPP Explorer Panel State
  const [explorerESPPPercent, setExplorerESPPPercent] = useState(10);
  const [explorerConfirmedESPPPercent, setExplorerConfirmedESPPPercent] = useState(0);
  const [explorerESPPPeriodsLoading, setExplorerESPPPeriodsLoading] = useState(true);
  const [explorerESPPPeriodsError, setExplorerESPPPeriodsError] = useState(false);
  const [explorerFirstESPPPanelCheckDone, setExplorerFirstESPPPanelCheckDone] = useState(false);
  const [explorerESPPPeriods, setExplorerESPPPeriods] = useState<ESPPPurchasePeriodType[]>([]);
  const [explorerPeriod, setExplorerPeriod] = useState<ESPPPurchasePeriodType | undefined>(undefined);
  //Explorer Count Up State
  const [explorerLastDrawnOfferingPrice, setExplorerLastDrawnOfferingPrice] = useState<number | undefined>(undefined);
  const [explorerLastDrawnPurchasePrice, setExplorerLastDrawnPurchasePrice] = useState<number | undefined>(undefined);
  const [explorerLastDrawnEffectivePurchasePrice, setExplorerLastDrawnEffectivePurchasePrice] = useState<
    number | undefined
  >(undefined);
  const [explorerLastDrawnStockValueAtPurchase, setExplorerLastDrawnStockValueAtPurchase] = useState<
    number | undefined
  >(undefined);
  const [explorerLastDrawnStockNum, setExplorerLastDrawnStockNum] = useState<number | undefined>(undefined);
  const [explorerLastDrawnCashContrib, setExplorerLastDrawnCashContrib] = useState<number | undefined>(undefined);
  const [explorerLastDrawnValueGained, setExplorerLastDrawnValueGained] = useState<number | undefined>(undefined);

  useEffect(() => {
    axios
      .get("/api/espp/periods")
      .then((response) => {
        setExplorerESPPPeriodsError(false);
        setExplorerESPPPeriodsLoading(false);
        const esppPeriods = inProcessESPPPeriodResponse(response);
        const nextPurchasePeriod = getNextPurchasePeriod(esppPeriods, dayjs());
        setExplorerPeriod(nextPurchasePeriod);
        setExplorerESPPPeriods(esppPeriods);
        setExplorerFirstESPPPanelCheckDone(true);
      })
      .catch((error) => {
        setExplorerESPPPeriodsError(true);
        setExplorerESPPPeriodsLoading(false);
        setExplorerESPPPeriods([]);
        if (process.env.NODE_ENV === "development") {
          console.error(error);
        }
      });
  }, []); // second argument means only rerun if something in that array changes

  // ESPP Panel State
  const [firstESPPPanelCheckDone, setFirstESPPPanelCheckDone] = useState(false);
  // const [userSymbolOption, setUserSymbolOption] = useState<undefined | SymbolOption>(undefined); // undefined means that the query for finding the data is running
  // const [firstUserSymbolCheckDone, setFirstUserSymbolCheckDone] = useState(false);
  const [esppPlanSelectorConfig, setESPPPlanSelectorConfig] = useState<FormConfig | undefined>(undefined);
  const [esppPlanSelectorState, setESPPPlanSelectorState] = useState<FormRowState[]>([]);
  const [defaultESPPPlanSelectorForm, setDefaultESPPPlanSelectorForm] = useState<FormConfig | undefined>(undefined);
  const [defaultESPPPlanSelectState, setDefaultESPPPlanSelectorState] = useState<FormRowState[]>([]);
  const [esppPlans, setESPPPlans] = React.useState<ESPP[]>([]);
  // const [detailsStockPrice, setDetailsStockPrice] = React.useState<number | undefined>(undefined); // undefined means that the query for finding the data is running
  const [esppPlanLoading, setESPPPlanLoading] = React.useState(false);
  const [esppPlanError, setESPPPlanError] = useState(false);
  const [esppDialogOpen, setESPPDialogOpen] = useState(false);
  const [esppPanelExternalStartAdd, setESPPPanelExternalStartAdd] = useState(false);
  const [esppPanelLastDrawnReferencePrice, setESPPPanelLastDrawnReferencePrice] = useState<number | undefined>(
    undefined
  );
  const [esppPanelLastDrawnVestedValue, setESPPPanelLastDrawnVestedValue] = useState<number | undefined>(undefined);
  const [esppPanelLastDrawnSharesSold, setESPPPanelLastDrawnSharesSold] = useState<number | undefined>(undefined);
  const [esppPanelLastDrawnPercentVested, setESPPPanelLastDrawnPercentVested] = useState<number | undefined>(undefined);
  const [esppPanelLastDrawnDaysUntilNextVest, setESPPPanelLastDrawnDaysUntilNextVest] = useState<number | undefined>(
    undefined
  );
  const [esppPanelLastDrawnTotalStockValue, setESPPPanelLastDrawnTotalStockValue] = useState<number | undefined>(
    undefined
  );

  useEffect(() => {
    if (props.user !== undefined) {
      axios
        .get("/api/espp/form")
        .then((response) => {
          setESPPPlanSelectorConfig({ ...response.data, rowStates: undefined });
          setDefaultESPPPlanSelectorForm({ ...response.data, rowStates: undefined });
          if ("rowStates" in response.data) {
            setDefaultESPPPlanSelectorState(response.data["rowStates"]);
            setESPPPlanSelectorState(response.data["rowStates"]);
          } else {
            setDefaultESPPPlanSelectorState(DefaultConfiguredFormState(response.data));
            setESPPPlanSelectorState(DefaultConfiguredFormState(response.data));
          }
        })
        .catch((error) => console.error(error));
    }
  }, [props.user, userSymbolOption]);

  useEffect(() => {
    if (props.user !== undefined) {
      setESPPPlanLoading(true);
      setESPPPlanError(false);
      axios
        .get("/api/espp")
        .then((response) => {
          setESPPPlanError(false);
          setESPPPlanLoading(false);
          const esppPlans = inProcessESPPResponse(response);
          setESPPPlans(esppPlans);
          setFirstESPPPanelCheckDone(true);
        })
        .catch((error) => {
          setESPPPlanLoading(false);
          setESPPPlanError(true);
          if (process.env.NODE_ENV === "development") {
            console.error(error);
          }
        });
    } else {
      setESPPPlans([]);
    }
  }, [lastStockPlanUpdateTime, props.user]); // second argument means only rerun if something in that array changes

  //Scenarios Panel State
  const [lastScenarioGraphUpdateTime, setLastScenarioGraphUpdateTime] = useState(dayjs());
  const [scenario, setScenario] = useState("");
  const [scenarioPlotHistoricalPeriod, setScenarioPlotHistoricalPeriod] = useState<PeriodType>("2 Yr");
  const [scenarioPlotFuturePeriod, setScenarioPlotFuturePeriod] = useState<PeriodType>("2 Yr");
  const [selectedScenarioList, setSelectedScenarioList] = useState<SelectedScenario[]>([]);
  const [scenarioOptionsConfigs, setScenarioOptionsConfigs] = useState<{
    [key: string]: FormConfig;
  }>({}); // TODO: better typing here
  const [firstScenarioPanelOptionConfigCheckDone, setFirstScenarioPanelOptionConfigCheckDone] = useState(false);
  const [firstConfiguredScenariosCheckDone, setFirstConfiguredScenariosCheckDone] = useState(false);
  const [scenarioOptionsRowStates, setScenarioOptionsRowStates] = useState<{ [key: string]: FormRowState[] }>({});
  const [scenarioLoading, setScenarioLoading] = useState(true);
  const [scenarioRetrievalErrored, setScenarioRetrievalErrored] = useState(false);
  const [scenarioDialogOpen, setScenarioDialogOpen] = useState(false);
  const [completeLoadingOnScenarioDialogClose, setCompleteLoadingOnScenarioDialogClose] = useState(false);
  const [closeScenarioDialogOnGraphUpdate, setCloseScenarioDialogOnGraphUpdate] = useState(false);
  const [scenariosPanelLastDrawnTotalValue, setScenariosPanelLastDrawnTotalValue] = useState<number | undefined>(
    undefined
  );
  const [scenariosPanelLastDrawnCoreStockValue, setScenariosPanelLastDrawnCoreStockValue] = useState<
    number | undefined
  >(undefined);
  const [scenariosPanelLastDrawnOtherStockValue, setScenariosPanelLastDrawnOtherStockValue] = useState<
    number | undefined
  >(undefined);
  const [scenariosPanelLastDrawnCashValue, setScenariosPanelLastDrawnCashValue] = useState<number | undefined>(
    undefined
  );

  // define some strategies panel state early
  const [selectedStrategyList, setSelectedStrategyList] = useState<SelectedStrategy[]>([]);
  const primaryStrategy = useMemo(() => {
    if (selectedStrategyList && selectedStrategyList.length > 0) {
      const primaryStrategyList = selectedStrategyList.filter((strategy) => strategy.primary);
      if (primaryStrategyList && primaryStrategyList.length > 0) {
        const primaryStrategy = primaryStrategyList[0];
        const primaryStrategyName = primaryStrategy.params[0].currentModeStates[0] as string;
        return primaryStrategyName || primaryStrategyList[0].strategy;
      }
    }
    return undefined;
  }, [selectedStrategyList]);

  const handleScenariosUpdate = () => setLastOverviewGraphUpdateTime(dayjs());

  useEffect(() => {
    if (selectedScenarioList === undefined) {
      return;
    }
    if (!firstConfiguredStrategiesCheckDone) {
      return;
    }

    setScenarioLoading(true);
    setScenarioRetrievalErrored(false);

    Promise.all(selectedScenarioList.map((scenario) => updateScenarioGraphIfNotUpdated(scenario, primaryStrategy)))
      .then(
        // TODO: error handle this for if a backend request fails? do we really want to wait on all promises?
        (newSelectedScenarioList) => {
          setScenarioRetrievalErrored(false);
          if (
            selectedScenarioList.length !== newSelectedScenarioList.length ||
            !selectedScenarioList.every(
              (scenario, index) =>
                scenario.scenarioDisplayProps.updated === newSelectedScenarioList[index].scenarioDisplayProps.updated
            )
          ) {
            setSelectedScenarioList(newSelectedScenarioList);
          } // TODO: improve only refresh on change...what if graph updates true to true with some minor tweak on edit?
        }
      )
      .catch((err) => {
        setScenarioRetrievalErrored(true);
        if (process.env.NODE_ENV === "development") {
          console.log(err);
        }
      })
      .finally(() => {
        if (closeScenarioDialogOnGraphUpdate) {
          setCompleteLoadingOnScenarioDialogClose(true);
          setScenarioDialogOpen(false);
          setCloseScenarioDialogOnGraphUpdate(false);
        } else {
          setScenarioLoading(false);
        }
      });
  }, [
    selectedScenarioList,
    setScenarioLoading,
    setSelectedScenarioList,
    setScenarioDialogOpen,
    closeScenarioDialogOnGraphUpdate,
    primaryStrategy,
    firstConfiguredStrategiesCheckDone,
  ]); // TODO: pass user log ins down to here and pull these on login. need to update these scenario models if a stock plan is added...

  useEffect(() => {
    setSelectedScenarioList((ssl) =>
      ssl
        ? ssl.map((scenario) => ({
            ...scenario,
            scenarioDisplayProps: { ...scenario.scenarioDisplayProps, updated: false },
          }))
        : []
    );
  }, [lastStockPlanUpdateTime, lastScenarioGraphUpdateTime]);

  useEffect(() => {
    axios
      .get("/api/scenario/configured")
      .then((response) => {
        const configuredScenarios = response.data.configuredScenarios;
        if (configuredScenarios && configuredScenarios.length > 0) {
          configuredScenarios.forEach((scenario: any) => {
            scenario.scenarioDisplayProps = defaultNoScenarioDisplayProps;
            scenario.currentlyDisplayed = scenario.primary;
          });
          setSelectedScenarioList(configuredScenarios);
        } else {
          setSelectedScenarioList([]);
        }

        setFirstConfiguredScenariosCheckDone(true);
      })
      .catch((error) => {
        if (process.env.NODE_ENV === "development") {
          console.error(error);
        }
      }); // TODO: better error handling
  }, []);

  useEffect(() => {
    axios
      .get("/api/scenario/options")
      .then((response) => {
        const responseScenarioOptionsConfigs = response.data;
        setScenarioOptionsConfigs(responseScenarioOptionsConfigs);
        var responseBackendState: { [key: string]: FormRowState[] } = {};
        Object.keys(responseScenarioOptionsConfigs).forEach(
          (key) => (responseBackendState[key] = DefaultConfiguredFormState(responseScenarioOptionsConfigs[key]))
        );
        setScenarioOptionsRowStates(responseBackendState);
        setFirstScenarioPanelOptionConfigCheckDone(true);
      })
      .catch((error) => {
        if (process.env.NODE_ENV === "development") {
          console.error(error);
        }
      }); // TODO: better error handling
  }, []);

  //Strategies Panel State
  const [strategy, setStrategy] = useState("");
  const [strategyPlotHistoricalPeriod, setStrategyPlotHistoricalPeriod] = useState<PeriodType>("2 Yr");

  const [strategyMetadata, setStrategyMetadata] = useState<{ [key: string]: any }>({});
  const [strategyOptionsConfigs, setStrategyOptionsConfigs] = useState<{
    [key: string]: FormConfig;
  }>({}); // TODO: better typing here
  const [firstStrategyPanelOptionConfigCheckDone, setFirstStrategyPanelOptionConfigCheckDone] = useState(false);
  const [strategyOptionsRowStates, setStrategyOptionsRowStates] = useState<{ [key: string]: FormRowState[] }>({});
  const [strategyLoading, setStrategyLoading] = useState(true);
  const [strategyRetrievalErrored, setStrategyRetrievalErrored] = useState(false);
  const [strategyDialogOpen, setStrategyDialogOpen] = useState(false);
  const [completeLoadingOnStrategyDialogClose, setCompleteLoadingOnStrategyDialogClose] = useState(false);
  const [closeStrategyDialogOnGraphUpdate, setCloseStrategyDialogOnGraphUpdate] = useState(false);
  const [strategiesPanelLastDrawnTotalValue, setStrategiesPanelLastDrawnTotalValue] = useState<number | undefined>(
    undefined
  );
  const [strategiesPanelLastDrawnCoreStockValue, setStrategiesPanelLastDrawnCoreStockValue] = useState<
    number | undefined
  >(undefined);
  const [strategiesPanelLastDrawnOtherStockValue, setStrategiesPanelLastDrawnOtherStockValue] = useState<
    number | undefined
  >(undefined);
  const [strategiesPanelLastDrawnCashValue, setStrategiesPanelLastDrawnCashValue] = useState<number | undefined>(
    undefined
  );

  const handleStrategiesUpdate = () => {
    setLastScenarioGraphUpdateTime(dayjs());
    setLastOverviewGraphUpdateTime(dayjs());
  };

  const currentlyDisplayedStrategy = useMemo(
    () =>
      selectedStrategyList
        ? (selectedStrategyList.find((strategy) => strategy.currentlyDisplayed)?.params[0]
            ?.currentModeStates[0] as string)
        : undefined,
    [selectedStrategyList]
  );

  const primaryStrategyFutureScenarioCompatible = useMemo(() => {
    if (primaryStrategy) {
      return primaryStrategy in strategyMetadata && strategyMetadata[primaryStrategy].scenario_compatible;
    }
    return false;
  }, [primaryStrategy, strategyMetadata]);

  useEffect(() => {
    if (selectedStrategyList === undefined) {
      return;
    }

    setStrategyLoading(true);
    setStrategyRetrievalErrored(false);

    Promise.all(selectedStrategyList.map(updateStrategyGraphIfNotUpdated))
      .then(
        // TODO: error handle this for if a backend request fails? do we really want to wait on all promises?
        (newSelectedStrategyList) => {
          setStrategyRetrievalErrored(false);
          if (
            selectedStrategyList.length !== newSelectedStrategyList.length ||
            !selectedStrategyList.every(
              (strategy, index) =>
                strategy.strategyDisplayProps.updated === newSelectedStrategyList[index].strategyDisplayProps.updated
            )
          ) {
            setSelectedStrategyList(newSelectedStrategyList);
          } // TODO: improve only refresh on change...what if graph updates true to true with some minor tweak on edit?
        }
      )
      .catch((err) => {
        setStrategyRetrievalErrored(true);
        if (process.env.NODE_ENV === "development") {
          console.log(err);
        }
      })
      .finally(() => {
        if (closeStrategyDialogOnGraphUpdate) {
          setCompleteLoadingOnStrategyDialogClose(true);
          setStrategyDialogOpen(false);
          setCloseStrategyDialogOnGraphUpdate(false);
        } else {
          setStrategyLoading(false);
        }
      });
  }, [selectedStrategyList, setStrategyLoading, setSelectedStrategyList, setStrategyDialogOpen]);

  useEffect(() => {
    setSelectedStrategyList((ssl) =>
      ssl
        ? ssl.map((strategy) => ({
            ...strategy,
            strategyDisplayProps: { ...strategy.strategyDisplayProps, updated: false },
          }))
        : []
    );
  }, [lastStockPlanUpdateTime]);

  useEffect(() => {
    axios
      .get("/api/strategy/configured")
      .then((response) => {
        const configuredStrategies = response.data.configuredStrategies;
        if (configuredStrategies && configuredStrategies.length > 0) {
          configuredStrategies.forEach((strategy: any) => {
            strategy.strategyDisplayProps = defaultNoStrategyDisplayProps;
            strategy.currentlyDisplayed = strategy.primary;
          });
          setSelectedStrategyList(configuredStrategies);
        } else {
          setSelectedStrategyList([]);
        }

        const strategyMetadata = response.data.strategy_metadata ?? {};
        setStrategyMetadata(strategyMetadata);

        setFirstConfiguredStrategiesCheckDone(true);
      })
      .catch((error) => {
        if (process.env.NODE_ENV === "development") {
          console.error(error);
        }
      }); // TODO: better error handling
  }, []);

  useEffect(() => {
    axios
      .get("/api/strategy/options")
      .then((response) => {
        const responseStrategyOptionsConfigs = response.data;
        setStrategyOptionsConfigs(responseStrategyOptionsConfigs);
        var responseBackendState: { [key: string]: FormRowState[] } = {};
        Object.keys(responseStrategyOptionsConfigs).forEach(
          (key) => (responseBackendState[key] = DefaultConfiguredFormState(responseStrategyOptionsConfigs[key]))
        );
        setStrategyOptionsRowStates(responseBackendState);
        setFirstStrategyPanelOptionConfigCheckDone(true);
      })
      .catch((error) => {
        if (process.env.NODE_ENV === "development") {
          console.error(error);
        }
      }); // TODO: better error handling
  }, []);

  //Profile Tab State
  const [profileConfig, setProfileConfig] = useState<FormConfig | undefined>(undefined);
  const [firstWithholdingCheckDone, setFirstWithholdingCheckDone] = useState(false);
  const [withholdingOn, setWithholdingOn] = useState(true);
  const [withholdingRate, setWithholdingRate] = useState(-1);
  const [inputRowStateList, setInputRowStateList] = useState<FormRowState[]>([]);

  function getProfileFromBackend() {
    axios
      .get("/api/userprofile/profile")
      .then((response) => {
        const profileFormConfig = { ...response.data, rowStates: undefined };
        setProfileConfig(profileFormConfig);
        setBackendInputRowStateList(response.data["rowStates"]);
        setInputRowStateList(response.data["rowStates"]);
      })
      .catch((error) => {
        if (process.env.NODE_ENV === "development") {
          console.error(error);
        }
      });
  } // TODO: better error handling}

  function getWithholdingFromBackend() {
    axios
      .get("/api/userprofile/withholding")
      .then((response) => {
        setWithholdingOn(response.data["withholding_enabled"]);
        setWithholdingRate(numberFromBackendStringMakeNullNegativeOne(response.data["withholding_rate"]));
        setFirstWithholdingCheckDone(true);
      })
      .catch((error) => {
        if (process.env.NODE_ENV === "development") {
          console.error(error);
        }
      });
  }

  useEffect(getProfileFromBackend, []);
  useEffect(getWithholdingFromBackend, []);

  const panelContents: {
    [key in PageOption]: any;
  } = {
    onboarding: <OnboardPanel />,
    overview: (
      <OverviewPanel
        plotProps={{
          plotLoading: !overviewPlotJsonAndUpdated.updated,
          pctVested: percentVested,
          lastDrawnPctVested: overviewPanelLastDrawnPercentVested,
          setLastDrawnPctVested: setOverviewPanelLastDrawnPercentVested,
          totalStockValue: totalStockValue,
          lastDrawnTotalStockValue: lastDrawnTotalStockValue,
          setLastDrawnTotalStockValue: setLastDrawnTotalStockValue,
          vestedValue: vestedValue,
          lastDrawnVestedValue: lastDrawnVestedValue,
          setLastDrawnVestedValue: setLastDrawnVestedValue,
          referencePrice: referencePrice,
          lastDrawnReferencePrice: lastDrawnReferencePrice,
          setLastDrawnReferencePrice: setLastDrawnReferencePrice,
          userSymbol: userSymbolOption?.symbol ?? "",
          referenceDateString: referenceDateString,
          overviewPlot: overviewPlotJsonAndUpdated.json,
          frequency: overviewPlotFrequency,
          setFrequency: setOverviewPlotFrequency,
          historicalPeriod: overviewPlotHistoricalPeriod,
          setHistoricalPeriod: setOverviewPlotHistoricalPeriod,
          futurePeriod: overviewPlotFuturePeriod,
          setFuturePeriod: setOverviewPlotFuturePeriod,
          updateOverviewGraph: () => setLastOverviewGraphUpdateTime(dayjs()),
          statsError: statisticsRetrievalErrored,
        }}
        secondaryStatProps={{
          plotLoading: !overviewPlotJsonAndUpdated.updated,
          daysUntilNextTrade: daysUntilNextTrade,
          nextTradeMoreInfoTitle: nextTradeMoreInfoTitle,
          nextTradeMoreInfo: nextTradeMoreInfoString,
          lastDrawnDaysUntilNextTrade: lastDrawnDaysUntilNextTrade,
          setLastDrawnDaysUntilNextTrade: setLastDrawnDaysUntilNextTrade,
          valueSold: valueSold,
          lastDrawnValueSold: lastDrawnValueSold,
          setLastDrawnValueSold: setLastDrawnValueSold,
          daysUntilNextVest: daysUntilNextVest,
          nextVestMoreInfoTitle: nextVestMoreInfoTitle,
          nextVestMoreInfo: nextVestMoreInfoString,
          lastDrawnDaysUntilNextVest: lastDrawnDaysUntilNextVest,
          setLastDrawnDaysUntilNextVest: setLastDrawnDaysUntilNextVest,
          statsError: statisticsRetrievalErrored,
        }}
        noStockPlansAdded={detailsStockPlans.length < 1}
        noESPPPlansAdded={esppPlans.length < 1}
        firstStockPlansCheckDone={firstDetailsPanelCheckDone && firstESPPPanelCheckDone}
        firstStrategiesCheckDone={firstConfiguredStrategiesCheckDone}
        numStrategiesConfigured={selectedStrategyList ? selectedStrategyList.length : 0}
        firstScenariosCheckDone={firstConfiguredScenariosCheckDone}
        numScenariosConfigured={selectedScenarioList ? selectedScenarioList.length : 0}
        setPage={props.setPage}
        plotError={overviewPlotError}
        immediateStockPlanAddPrompt={() => setDetailsPanelExternalStartAdd(true)}
        immediateESPPAddPrompt={() => setESPPPanelExternalStartAdd(true)}
        selectedStrategyName={primaryStrategy}
        selectedScenarioName={useMemo(() => {
          if (selectedScenarioList && selectedScenarioList.length > 0) {
            const primaryScenarioList = selectedScenarioList.filter((scenario) => scenario.primary);
            if (primaryScenarioList && primaryScenarioList.length > 0) {
              const primaryScenario = primaryScenarioList[0];
              const primaryScenarioName = primaryScenario.params[0].currentModeStates[0] as string;
              return primaryScenarioName || primaryScenarioList[0].scenario;
            }
          }
          return undefined;
        }, [selectedScenarioList])}
        withholdingOn={withholdingOn}
        withholdingRate={withholdingRate}
        firstWithholdingCheckDone={firstWithholdingCheckDone}
      />
    ),
    "stock-plans": (
      <DetailsPanel
        // setConfirmOnNavigateAway={props.setConfirmOnNavigateAway}
        stockPlans={detailsStockPlans}
        stockPrice={detailsStockPrice}
        lastDrawnReferencePrice={detailsPanelLastDrawnReferencePrice}
        setLastDrawnReferencePrice={setDetailsPanelLastDrawnReferencePrice}
        referenceDateString={referenceDateString}
        handleUpdateNotification={() => setLastStockPlanUpdateTime(dayjs())}
        handleSymbolAdd={getProfileFromBackend}
        dialogOpen={detailsDialogOpen}
        setDialogOpen={setDetailsDialogOpen}
        stockPlanError={detailsStockPlanError}
        userSymbolOption={userSymbolOption}
        daysUntilNextVest={daysUntilNextVestRSU}
        lastDrawnDaysUntilNextVest={detailsPanelLastDrawnDaysUntilNextVest}
        setLastDrawnDaysUntilNextVest={setDetailsPanelLastDrawnDaysUntilNextVest}
        vestedValue={vestedValueRSU}
        vestedValueGross={vestedValueGrossRSU}
        lastDrawnVestedValue={detailsPanelLastDrawnVestedValue}
        setLastDrawnVestedValue={setDetailsPanelLastDrawnVestedValue}
        totalStockValue={totalStockValueRSU}
        totalStockValueGross={totalStockValueGrossRSU}
        lastDrawnTotalStockValue={detailsPanelLastDrawnTotalStockValue}
        setLastDrawnTotalStockValue={setDetailsPanelLastDrawnTotalStockValue}
        totalSharesSold={totalSharesSoldRSU}
        lastDrawnSharesSold={detailsPanelLastDrawnSharesSold}
        setLastDrawnSharesSold={setDetailsPanelLastDrawnSharesSold}
        percentVested={percentVestedRSU}
        lastDrawnPercentVested={detailsPanelLastDrawnPercentVested}
        setLastDrawnPercentVested={setDetailsPanelLastDrawnPercentVested}
        statsError={statisticsRetrievalErrored}
        firstCheckDone={firstDetailsPanelCheckDone && firstWithholdingCheckDone}
        promptForSymbol={dialogPromptForSymbol}
        finishPromptForSymbol={() => setDialogPromptForSymbol(false)}
        firstUserSymbolCheckDone={firstUserSymbolCheckDone}
        stockPlanSelectorConfig={stockPlanSelectorConfig}
        setStockPlanSelectorConfig={setStockPlanSelectorConfig}
        defaultStockPlanSelectorForm={defaultStockPlanSelectorForm}
        defaultStockPlanSelectState={defaultStockPlanSelectState}
        stockPlanSelectorState={stockPlanSelectorState}
        setStockPlanSelectorState={setStockPlanSelectorState}
        externalStartAdd={detailsPanelExternalStartAdd}
        clearExternalStartAdd={() => setDetailsPanelExternalStartAdd(false)}
        withholdingOn={withholdingOn}
        withholdingRate={withholdingRate}
        firstWithholdingCheckDone={firstWithholdingCheckDone}
      />
    ),
    espps: (
      <ESPPPanel
        esppPlans={esppPlans}
        stockPrice={detailsStockPrice}
        statsError={statisticsRetrievalErrored}
        lastDrawnReferencePrice={esppPanelLastDrawnReferencePrice}
        setLastDrawnReferencePrice={setESPPPanelLastDrawnReferencePrice}
        referenceDateString={referenceDateString}
        handleUpdateNotification={() => setLastStockPlanUpdateTime(dayjs())}
        handleSymbolAdd={getProfileFromBackend}
        dialogOpen={esppDialogOpen}
        setDialogOpen={setESPPDialogOpen}
        esppPlanError={esppPlanError}
        userSymbolOption={userSymbolOption}
        vestedValue={vestedValueESPP}
        vestedValueGross={vestedValueGrossESPP}
        lastDrawnVestedValue={esppPanelLastDrawnVestedValue}
        setLastDrawnVestedValue={setESPPPanelLastDrawnVestedValue}
        totalStockValue={totalStockValueESPP}
        totalStockValueGross={totalStockValueGrossESPP}
        lastDrawnTotalStockValue={esppPanelLastDrawnTotalStockValue}
        setLastDrawnTotalStockValue={setESPPPanelLastDrawnTotalStockValue}
        percentVested={percentVestedESPP}
        lastDrawnPercentVested={esppPanelLastDrawnPercentVested}
        setLastDrawnPercentVested={setESPPPanelLastDrawnPercentVested}
        totalSharesSold={totalSharesSoldESPP}
        lastDrawnSharesSold={esppPanelLastDrawnSharesSold}
        setLastDrawnSharesSold={setESPPPanelLastDrawnSharesSold}
        daysUntilNextVest={daysUntilNextVestESPP}
        lastDrawnDaysUntilNextVest={esppPanelLastDrawnDaysUntilNextVest}
        setLastDrawnDaysUntilNextVest={setESPPPanelLastDrawnDaysUntilNextVest}
        firstCheckDone={firstESPPPanelCheckDone && firstWithholdingCheckDone}
        promptForSymbol={dialogPromptForSymbol}
        finishPromptForSymbol={() => setDialogPromptForSymbol(false)}
        firstUserSymbolCheckDone={firstUserSymbolCheckDone}
        esppPlanSelectorConfig={esppPlanSelectorConfig}
        setESPPPlanSelectorConfig={setESPPPlanSelectorConfig}
        esppPlanSelectorState={esppPlanSelectorState}
        setESPPPlanSelectorState={setESPPPlanSelectorState}
        defaultESPPPlanSelectorForm={defaultESPPPlanSelectorForm}
        defaultESPPPlanSelectState={defaultESPPPlanSelectState}
        externalStartAdd={esppPanelExternalStartAdd}
        clearExternalStartAdd={() => setESPPPanelExternalStartAdd(false)}
      />
    ),
    "espp-explorer": (
      <ESPPExplorerPanel
        addSnackbarMessage={addSnackbarMessage}
        setPage={props.setPage}
        ESPPPercent={explorerESPPPercent}
        setESPPPercent={setExplorerESPPPercent}
        confirmedESPPPercent={explorerConfirmedESPPPercent}
        setConfirmedESPPPercent={setExplorerConfirmedESPPPercent}
        ESPPPeriods={explorerESPPPeriods}
        period={explorerPeriod}
        setPeriod={setExplorerPeriod}
        lastDrawnOfferingPrice={explorerLastDrawnOfferingPrice}
        setLastDrawnOfferingPrice={setExplorerLastDrawnOfferingPrice}
        lastDrawnPurchasePrice={explorerLastDrawnPurchasePrice}
        setLastDrawnPurchasePrice={setExplorerLastDrawnPurchasePrice}
        lastDrawnEffectivePurchasePrice={explorerLastDrawnEffectivePurchasePrice}
        setLastDrawnEffectivePurchasePrice={setExplorerLastDrawnEffectivePurchasePrice}
        lastDrawnStockValueAtPurchase={explorerLastDrawnStockValueAtPurchase}
        setLastDrawnStockValueAtPurchase={setExplorerLastDrawnStockValueAtPurchase}
        lastDrawnStockNum={explorerLastDrawnStockNum}
        setLastDrawnStockNum={setExplorerLastDrawnStockNum}
        lastDrawnCashContrib={explorerLastDrawnCashContrib}
        setLastDrawnCashContrib={setExplorerLastDrawnCashContrib}
        lastDrawnValueGained={explorerLastDrawnValueGained}
        setLastDrawnValueGained={setExplorerLastDrawnValueGained}
      />
    ),
    forecasts: (
      <ScenariosPanel
        addSnackbarMessage={addSnackbarMessage}
        firstChecksDone={
          firstScenarioPanelOptionConfigCheckDone &&
          firstConfiguredScenariosCheckDone &&
          firstDetailsPanelCheckDone &&
          firstESPPPanelCheckDone
        }
        scenario={scenario}
        setScenario={setScenario}
        selectedScenarioList={selectedScenarioList}
        setSelectedScenarioList={setSelectedScenarioList}
        primaryStrategy={primaryStrategy}
        strategyMetadata={strategyMetadata}
        scenarioOptionsConfigs={scenarioOptionsConfigs}
        scenarioOptionsRowStates={scenarioOptionsRowStates}
        setScenarioOptionsRowStates={setScenarioOptionsRowStates}
        loading={
          scenarioLoading ||
          !(
            firstScenarioPanelOptionConfigCheckDone &&
            firstConfiguredScenariosCheckDone &&
            firstDetailsPanelCheckDone &&
            firstESPPPanelCheckDone
          )
        }
        setLoading={setScenarioLoading}
        dialogOpen={scenarioDialogOpen}
        setDialogOpen={setScenarioDialogOpen}
        setCloseDialogOnGraphUpdate={setCloseScenarioDialogOnGraphUpdate}
        completeLoadingOnDialogClose={completeLoadingOnScenarioDialogClose}
        setCompleteLoadingOnDialogClose={setCompleteLoadingOnScenarioDialogClose}
        scenarioRetrievalErrored={scenarioRetrievalErrored}
        historicalPeriod={scenarioPlotHistoricalPeriod}
        setHistoricalPeriod={setScenarioPlotHistoricalPeriod}
        futurePeriod={scenarioPlotFuturePeriod}
        setFuturePeriod={setScenarioPlotFuturePeriod}
        lastDrawnTotalValue={scenariosPanelLastDrawnTotalValue}
        setLastDrawnTotalValue={setScenariosPanelLastDrawnTotalValue}
        lastDrawnCoreStockValue={scenariosPanelLastDrawnCoreStockValue}
        setLastDrawnCoreStockValue={setScenariosPanelLastDrawnCoreStockValue}
        lastDrawnOtherStockValue={scenariosPanelLastDrawnOtherStockValue}
        setLastDrawnOtherStockValue={setScenariosPanelLastDrawnOtherStockValue}
        lastDrawnCashValue={scenariosPanelLastDrawnCashValue}
        setLastDrawnCashValue={setScenariosPanelLastDrawnCashValue}
        noStockPlansAdded={detailsStockPlans.length < 1}
        noESPPPlansAdded={esppPlans.length < 1}
        setPage={props.setPage}
        immediateStockPlanAddPrompt={() => setDetailsPanelExternalStartAdd(true)}
        immediateESPPAddPrompt={() => setESPPPanelExternalStartAdd(true)}
        handleOverviewUpdateNotification={handleScenariosUpdate}
        withholdingOn={withholdingOn}
        withholdingRate={withholdingRate}
        firstWithholdingCheckDone={firstWithholdingCheckDone}
      />
    ),
    strategies: (
      <StrategiesPanel
        addSnackbarMessage={addSnackbarMessage}
        firstChecksDone={
          firstStrategyPanelOptionConfigCheckDone &&
          firstConfiguredStrategiesCheckDone &&
          firstDetailsPanelCheckDone &&
          firstESPPPanelCheckDone
        }
        strategy={strategy}
        setStrategy={setStrategy}
        selectedStrategyList={selectedStrategyList}
        setSelectedStrategyList={setSelectedStrategyList}
        setStrategyMetadata={setStrategyMetadata}
        strategyOptionsConfigs={strategyOptionsConfigs}
        strategyOptionsRowStates={strategyOptionsRowStates}
        setStrategyOptionsRowStates={setStrategyOptionsRowStates}
        loading={
          strategyLoading ||
          !(
            firstStrategyPanelOptionConfigCheckDone &&
            firstConfiguredStrategiesCheckDone &&
            firstDetailsPanelCheckDone &&
            firstESPPPanelCheckDone
          )
        }
        setLoading={setStrategyLoading}
        dialogOpen={strategyDialogOpen}
        setDialogOpen={setStrategyDialogOpen}
        setCloseDialogOnGraphUpdate={setCloseStrategyDialogOnGraphUpdate}
        completeLoadingOnDialogClose={completeLoadingOnStrategyDialogClose}
        setCompleteLoadingOnDialogClose={setCompleteLoadingOnStrategyDialogClose}
        strategyRetrievalErrored={strategyRetrievalErrored}
        historicalPeriod={strategyPlotHistoricalPeriod}
        setHistoricalPeriod={setStrategyPlotHistoricalPeriod}
        lastDrawnTotalValue={strategiesPanelLastDrawnTotalValue}
        setLastDrawnTotalValue={setStrategiesPanelLastDrawnTotalValue}
        lastDrawnCoreStockValue={strategiesPanelLastDrawnCoreStockValue}
        setLastDrawnCoreStockValue={setStrategiesPanelLastDrawnCoreStockValue}
        lastDrawnOtherStockValue={strategiesPanelLastDrawnOtherStockValue}
        setLastDrawnOtherStockValue={setStrategiesPanelLastDrawnOtherStockValue}
        lastDrawnCashValue={strategiesPanelLastDrawnCashValue}
        setLastDrawnCashValue={setStrategiesPanelLastDrawnCashValue}
        noStockPlansAdded={detailsStockPlans.length < 1}
        noESPPPlansAdded={esppPlans.length < 1}
        setPage={props.setPage}
        immediateStockPlanAddPrompt={() => setDetailsPanelExternalStartAdd(true)}
        immediateESPPAddPrompt={() => setESPPPanelExternalStartAdd(true)}
        handleUpdateNotification={handleStrategiesUpdate}
        withholdingOn={withholdingOn}
        withholdingRate={withholdingRate}
        firstWithholdingCheckDone={firstWithholdingCheckDone}
      />
    ),
    trade: <TradePanel />,
    tax: null,
    learn: <LearnPanel />,
    plaid: null, // <PlaidPanel />,
    profile: (
      <ProfilePanel
        setConfirmOnNavigateAway={props.setConfirmOnNavigateAway}
        userFullName={props.user}
        emailVerified={props.emailVerified}
        backendInputRowStateList={backendInputRowStateList}
        setBackendInputRowStateList={setBackendInputRowStateList}
        profileConfig={profileConfig}
        inputRowStateList={inputRowStateList}
        setInputRowStateList={setInputRowStateList}
        handleUpdateNotification={() => {
          axios
            .post(
              "/api/stockplan/validate",
              { old_symbol: userSymbolOption?.symbol, old_profile: backendInputRowStateList },
              {
                headers: { "X-CSRFToken": GetCSRFToken() },
              }
            )
            .then((response) => {
              addSnackbarMessage(
                response.data["snackbar"]["title"],
                response.data["snackbar"]["message"],
                response.data["snackbar"]["severity"]
              );
              if (response.data["rollback"] === true) {
                setInputRowStateList(backendInputRowStateList || []);
              } else {
                setBackendInputRowStateList(inputRowStateList);
                getWithholdingFromBackend();
                setLastStockPlanUpdateTime(dayjs());
              }
            })
            .catch((error) => {
              setInputRowStateList(backendInputRowStateList || []);
              addSnackbarMessage(
                "Oops! Error",
                "An error occurred while saving your profile. Please try again later.",
                "error"
              );
            });
        }}
      />
    ),
    theme: <ThemeTestPanel />,
    form: <FormTestPanel />,
  };

  const step1Status = useTourStep({
    selector: ".highlight-for-details-tour-step-1",
    startOpenOnce:
      firstDetailsPanelCheckDone && detailsStockPlans.length === 0 && firstESPPPanelCheckDone && esppPlans.length === 0,
    neverOpenWhen:
      props.skipTour ||
      !firstDetailsPanelCheckDone ||
      !firstESPPPanelCheckDone ||
      detailsStockPlanLoading ||
      detailsDialogOpen ||
      props.currentPage !== "stock-plans",
    completedWhenExternalCondition: detailsStockPlans.length > 0 || esppPlans.length > 0,
    completedWhenNextButtonClicked: false,
    nextButtonText: "Add Stock Plan",
    setNextButtonText: props.setNextButtonForTourStepText,
    nextButtonAction: () => setDetailsPanelExternalStartAdd(true),
    setNextButtonAction: props.nextButtonForTourStepAction,
    hasForkedTour: true,
    setHasForkedTour: props.setHasForkedTour,
    tourForkAction: () => {
      setESPPPanelExternalStartAdd(true);
      props.setPage("espps");
    },
    setTourForkAction: props.setTourForkAction,
    tourForkButtonText: "Add ESPP",
    setTourForkButtonText: props.setTourForkButtonText,
    necessaryPreviousStepStatus: { status: "completed" as const, isComplete: true },
    restartStepIfCompletionUndone: false,
    title: "Add a Stock Plan (4 min)",
    description: [
      "Stocks are complicated, use Salus magic entry to demystify and manage your company stock.",
      "",
      "Even if a company has paid you a 'stock grant' or 'bonus', the details of the plan are often different than they seem. You don't actually own or earn the stock until the shares vest which can take years and come with unexpected taxes. Once stock vests, managing it is a whole other challenge. You can sell it, hold it, or diversify it into other investments, but the tools available today aren't designed to help you navigate this challenge.",
      "",
      "Instead of trying to become a compensation, tax, stock market, and finance expert, trust Salus to help demystify this process and have our proprietary system help you meet your goals.",
    ],
  });

  const step1bStatus = useTourStep({
    selector: ".highlight-for-espp-tour-step-1",
    startOpenOnce:
      firstDetailsPanelCheckDone && detailsStockPlans.length === 0 && firstESPPPanelCheckDone && esppPlans.length === 0,
    neverOpenWhen:
      props.skipTour ||
      !firstDetailsPanelCheckDone ||
      !firstESPPPanelCheckDone ||
      esppPlanLoading ||
      esppDialogOpen ||
      props.currentPage !== "espps",
    completedWhenExternalCondition: detailsStockPlans.length > 0 || esppPlans.length > 0,
    completedWhenNextButtonClicked: false,
    nextButtonText: "Add Stock Plan",
    setNextButtonText: props.setNextButtonForTourStepText,
    nextButtonAction: () => {
      setDetailsPanelExternalStartAdd(true);
      props.setPage("stock-plans");
    },
    setNextButtonAction: props.nextButtonForTourStepAction,
    hasForkedTour: true,
    setHasForkedTour: props.setHasForkedTour,
    tourForkAction: () => {
      setESPPPanelExternalStartAdd(true);
    },
    setTourForkAction: props.setTourForkAction,
    tourForkButtonText: "Add ESPP",
    setTourForkButtonText: props.setTourForkButtonText,
    necessaryPreviousStepStatus: { status: "completed" as const, isComplete: true },
    restartStepIfCompletionUndone: false,
    title: "Add a Stock Plan (4 min)",
    description: [
      "Stocks are complicated, use Salus magic entry to demystify and manage your company stock.",
      "",
      "Even if a company has paid you a 'stock grant' or 'bonus', the details of the plan are often different than they seem. You don't actually own or earn the stock until the shares vest which can take years and come with unexpected taxes. Once stock vests, managing it is a whole other challenge. You can sell it, hold it, or diversify it into other investments, but the tools available today aren't designed to help you navigate this challenge.",
      "",
      "Instead of trying to become a compensation, tax, stock market, and finance expert, trust Salus to help demystify this process and have our proprietary system help you meet your goals.",
    ],
  });

  const step2Status = useTourStep({
    selector: ".highlight-for-details-tour-step-2",
    startOpenOnce: detailsStockPlans.length > 0,
    neverOpenWhen:
      props.skipTour || !firstDetailsPanelCheckDone || detailsDialogOpen || props.currentPage !== "stock-plans",
    completedWhenExternalCondition: false,
    completedWhenNextButtonClicked: true,
    nextButtonText: "View Overview",
    setNextButtonText: props.setNextButtonForTourStepText,
    nextButtonAction: () => props.setPage("overview"),
    setNextButtonAction: props.nextButtonForTourStepAction,
    necessaryPreviousStepStatus: step1Status,
    restartStepIfCompletionUndone: false,
    title: "Your Stock Plan Over Time",
    description: [
      "Here's how your stock plan evolves over time, and what it means for you.",
      "",
      "• The pinned left column shows how many vested shares you are still holding and their current market value.",
      "• The middle columns show how your vesting evolves, including the price at vest of any already vested stock.",
      "• The pinned right column shows all your shares, their current market value, and how much market value is in unvested shares.",
      "",
      "Cumulative includes everything up to each date while incremental shows the change at each date. Add more stock plans now or come back later.",
    ],
  });

  const step2bStatus = useTourStep({
    selector: ".highlight-for-espp-tour-step-2",
    startOpenOnce: esppPlans.length > 0,
    neverOpenWhen: props.skipTour || !firstESPPPanelCheckDone || esppDialogOpen || props.currentPage !== "espps",
    completedWhenExternalCondition: false,
    completedWhenNextButtonClicked: true,
    nextButtonText: "View Overview",
    setNextButtonText: props.setNextButtonForTourStepText,
    nextButtonAction: () => props.setPage("overview"),
    setNextButtonAction: props.nextButtonForTourStepAction,
    necessaryPreviousStepStatus: step1Status,
    restartStepIfCompletionUndone: false,
    title: "Details of your ESPP",
    description: [
      "ESPPs are a great way to save money and invest in your company.",
      "",
      "They are often a great deal, but they can be complicated. Use this table to quickly see all the details of your ESPPs.",
    ],
  });

  const step3Status = useTourStep({
    selector: ".highlight-for-overview-tour-step-1",
    startOpenOnce: true, // open on first visit to overview page until step is completed
    neverOpenWhen: props.skipTour || props.currentPage !== "overview",
    completedWhenExternalCondition: false,
    completedWhenNextButtonClicked: true,
    nextButtonText: "Next",
    setNextButtonText: props.setNextButtonForTourStepText,
    nextButtonAction: () => null,
    setNextButtonAction: props.nextButtonForTourStepAction,
    necessaryPreviousStepStatus: step1Status,
    restartStepIfCompletionUndone: false,
    title: "Your Portfolio at a Glance",
    skipTerms: ["vest"],
    description: [
      "Here are the most important stats about your equity, using the most recent company stock price.",
      "",
      "• Percent Vested is what percent you own of your total stock grants.",
      "• Available Value represents just the share of your equity that is already vested and what is available to you today.",
      // "• Vested Stock Sold is how much of your equity you've sold.",
      // "• Next Vesting in tells you how far out your next vest event is.",
      "• Total Value includes both vested and unvested equity, using current market conditions.",
      "• Stock Price is the most recent close price of your company's stock.",
    ],
  });

  const step4Status = useTourStep({
    selector: ".highlight-for-overview-tour-step-2",
    startOpenOnce: true,
    neverOpenWhen: props.skipTour || props.currentPage !== "overview",
    completedWhenExternalCondition: false,
    completedWhenNextButtonClicked: true,
    nextButtonText: "View Strategies",
    setNextButtonText: props.setNextButtonForTourStepText,
    nextButtonAction: () => {
      setSelectedStrategyList((ssl) =>
        ssl ? ssl.map((strategy) => ({ ...strategy, currentlyDisplayed: false })) : []
      );
      props.setPage("strategies");
    },
    setNextButtonAction: props.nextButtonForTourStepAction,
    necessaryPreviousStepStatus: step3Status,
    restartStepIfCompletionUndone: false,
    title: "Your Portfolio over Time",
    description: [
      "Your portfolio is made up of many components, each moving every day with public market movements, vesting events, and your strategy trades.",
      "",
      "Here are all the pieces:",
      "• Yellow triangles represent moments in time where stock vests or a strategy would have made a trade for you.",
      "• Solid green area represents the vested equity from your various equity plans.",
      "• Dashed green area represents equity from your plans that will vest in the future and assumes the value of the stock follows a forecast you can configure and select.",
      "• Dashed white area represents your unvested stock at any point in time, again assuming that the future stock price follows your selected forecast.",
      "• Other areas like blue and orange represent your cash and other stock holdings under your previous stock sales and selected strategy in the future.",
    ],
    position: props.drawerWidth,
  });

  const anyStrategySelected =
    selectedStrategyList && selectedStrategyList.some((strategy) => strategy.currentlyDisplayed);
  const selectedStrategyName =
    selectedStrategyList?.find((strategy) => strategy.currentlyDisplayed)?.strategy || undefined;

  const step5Status = useTourStep({
    selector: ".highlight-for-strategies-tour-step-1",
    startOpenOnce: !anyStrategySelected,
    neverOpenWhen: props.skipTour || strategyDialogOpen || anyStrategySelected || props.currentPage !== "strategies",
    completedWhenExternalCondition: anyStrategySelected && !strategyLoading,
    completedWhenNextButtonClicked: false,
    nextButtonText: "Add Suggestions",
    setNextButtonText: props.setNextButtonForTourStepText,
    nextButtonAction: () => {
      const strategySuggestions = StrategySuggestions(selectedStrategyList);
      const newSelectedStrategyList = [
        ...(selectedStrategyList
          ? selectedStrategyList.map((strategy) => ({ ...strategy, currentlyDisplayed: false }))
          : []),
        ...strategySuggestions.suggestions,
      ];
      setSelectedStrategyList(newSelectedStrategyList);
      if (strategySuggestions.snackbarPrimaryName !== undefined) {
        snackbarStrategyChange(addSnackbarMessage, strategySuggestions.snackbarPrimaryName);
      }
      PostSelectedStrategyList(newSelectedStrategyList, setStrategyMetadata, handleStrategiesUpdate);
    },
    setNextButtonAction: props.nextButtonForTourStepAction,
    // onCompletion: () => props.setPage("trade"),
    necessaryPreviousStepStatus: step4Status,
    restartStepIfCompletionUndone: false,
    title: "Explore Some Strategies",
    description: [
      "Here you can see and explore the strategies that can be configured for managing your stock plans.",
      "",
      "Read more about each one and click the plus to configure your parameters to explore. You might need to scroll to see all " +
        (Object.keys(strategyOptionsConfigs).length || "the") +
        " options.",
    ],
    position: props.drawerWidth,
  });

  const step6Status = useTourStep({
    selector: ".highlight-for-strategies-tour-step-2",
    startOpenOnce: anyStrategySelected && !strategyLoading,
    neverOpenWhen: props.skipTour || strategyDialogOpen || !anyStrategySelected || props.currentPage !== "strategies",
    completedWhenExternalCondition: false,
    completedWhenNextButtonClicked: true,
    nextButtonText: "View Forecasts",
    setNextButtonText: props.setNextButtonForTourStepText,
    nextButtonAction: () => {
      setSelectedScenarioList((ssl) =>
        ssl ? ssl.map((scenario) => ({ ...scenario, currentlyDisplayed: false })) : []
      );

      props.setPage("forecasts");
    },
    setNextButtonAction: props.nextButtonForTourStepAction,
    necessaryPreviousStepStatus: step5Status,
    restartStepIfCompletionUndone: false,
    title: "Historical Strategy Performance",
    skipTerms: ["vest"],
    description: [
      "Here you can see how a " +
        (selectedStrategyName + " " || "") +
        "strategy would have done if managing your equity compensation from day 1.",
      "",
      "Use these charts to learn about different strategy options you can set and what their parameters do:",
      "• Yellow triangles indicate moments in time where stock vests or the strategy would have made a trade for you in the past.",
      "• The stacked area charts represent the total portfolio value over time and uses the left-side y-axis. Including your stock plan stock, another stock you might diversify into (like SPY for the SP500 or QQQ for the NASDAQ100), and cash value you would've accumulated from selling shares just for cash.",
      "• The single green line is the stock price over time and uses the right-side y-axis.",
      "",
      "Once these modeled trades align with your future goals, lock in a strategy and manage your equity in the future.",
    ],
    position: props.drawerWidth,
  });

  const anyScenarioSelected =
    selectedScenarioList && selectedScenarioList.some((scenario) => scenario.currentlyDisplayed);
  const selectedScenarioName =
    selectedScenarioList?.find((scenario) => scenario.currentlyDisplayed)?.scenario || undefined;

  const step7Status = useTourStep({
    selector: ".highlight-for-scenarios-tour-step-1",
    startOpenOnce: !anyScenarioSelected,
    neverOpenWhen: props.skipTour || scenarioDialogOpen || anyScenarioSelected || props.currentPage !== "forecasts",
    completedWhenExternalCondition: anyScenarioSelected && !scenarioLoading,
    completedWhenNextButtonClicked: false,
    nextButtonText: "Add Suggestions",
    setNextButtonText: props.setNextButtonForTourStepText,
    nextButtonAction: () => {
      const scenarioSuggestions = ScenarioSuggestions(selectedScenarioList);
      const newSelectedScenarioList = [
        ...(selectedScenarioList
          ? selectedScenarioList.map((scenario) => ({ ...scenario, currentlyDisplayed: false }))
          : []),
        ...scenarioSuggestions.suggestions,
      ];
      setSelectedScenarioList(newSelectedScenarioList);
      if (scenarioSuggestions.snackbarPrimaryName !== undefined) {
        snackbarScenarioChange(addSnackbarMessage, scenarioSuggestions.snackbarPrimaryName);
      }
      PostSelectedScenarioList(newSelectedScenarioList, handleScenariosUpdate);
    },
    setNextButtonAction: props.nextButtonForTourStepAction,
    // onCompletion: () => props.setPage("trade"),
    necessaryPreviousStepStatus: step6Status,
    restartStepIfCompletionUndone: false,
    title: "Explore Some Forecasts",
    description: [
      "Here you can codify your forecasts and expectations for your company stock.",
      "",
      "Read more about each option and click the plus to configure your parameters to explore. You might need to scroll to see all " +
        (Object.keys(scenarioOptionsConfigs).length || "the") +
        " options.",
    ],
    position: props.drawerWidth,
  });

  const step8Status = useTourStep({
    selector: ".highlight-for-scenarios-tour-step-2",
    startOpenOnce: anyScenarioSelected && !scenarioLoading,
    neverOpenWhen: props.skipTour || scenarioDialogOpen || !anyScenarioSelected || props.currentPage !== "forecasts",
    completedWhenExternalCondition: false,
    completedWhenNextButtonClicked: true,
    nextButtonText: "Finish Tour",
    setNextButtonText: props.setNextButtonForTourStepText,
    nextButtonAction: () => {
      props.setPage("overview");
      props.finishTour();
    },
    setNextButtonAction: props.nextButtonForTourStepAction,
    necessaryPreviousStepStatus: step7Status,
    restartStepIfCompletionUndone: false,
    title: "Future Stock Projection and Strategy Performance",
    description: [
      "See how your portfolio evolves with each of your forecasts while using your selected strategy to make trades.",
      "",
      "Use these charts to learn about different future forecast options you can set and what their parameters do:",
      "• Yellow triangles indicate moments in time where stock vests or your selected strategy would make a trade for you in the future under this future stock forecast.",
      "• The single green line is the stock price over time and uses the right-side y-axis. For dates after today, it is modeled based on the forecast selected.",
      "• The stacked area charts represent the total portfolio value over time and uses the left-side y-axis. Including your stock plan stock, another stock you might diversify into (like SPY for the SP500 or QQQ for the NASDAQ100), and cash value you would've accumulated from selling shares just for cash.",
    ],
    position: props.drawerWidth,
  });

  return (
    <>
      {AlertMultiMessageSnackbarComponent}
      <Routes>
        {/* special case for index route */}
        <Route index element={panelContents["overview"]} />
        {Object.entries(panelContents).map(([panel_name, panel_content]) =>
          props.validAppRoutes.includes(panel_name as PageOption) ? (
            <Route key={panel_name.toLowerCase()} path={panel_name.toLowerCase()} element={panel_content} />
          ) : null
        )}
        <Route path="*" element={<ErrorPage moveLeftBy={props.drawerWidth} />} />
      </Routes>
    </>
  );
}

export default MainPanel;
